import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type GalleryItemData = {
  title?: string | null;
  name?: string | null;
  year?: string | null;
  medium?: string | null;
  price?: string | null;
  size?: string | null;
  img?: string | null;
};

export type ModalData = {
  imgSrc: string;
  imgAlt: string;
  title: string;
  details: string[]; // ordered, deduped list for rendering
};

/**
 * useGalleryModal
 * Encapsulates the behavior from the original art.html: when a .gallery-item
 * is clicked, open a modal populated from data-* attributes, with details
 * depending on the active filter button when present.
 */
export function useGalleryModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null as ModalData | null);
  // Avoid TS generics to fit current setup
  const itemsRef: any = useRef([]);
  const indexRef: any = useRef(-1);

  const openFromElement = useCallback((itemEl: HTMLElement) => {
    // Resolve the current list of items from the DOM (respects current filters)
    const nodeList = Array.from(document.querySelectorAll('.gallery-grid .gallery-item')) as any[];
    itemsRef.current = nodeList as any;
    indexRef.current = nodeList.findIndex((el: any) => el === itemEl);

    const get = (attr: string) => itemEl.getAttribute(attr);

    const img = get('data-img') || '';
    const title = get('data-title') || '';
    const category = get('data-category') || '';

    // Build ordered, de-duplicated details
    // Show the actual category of the image instead of "All"
    // Details exclude the title/name (shown separately) to prevent duplication.
    const rawDetails = [
      get('data-medium') || '',
      get('data-size') || '',
      get('data-year') || '',
      category, // actual category
    ]
      .map(v => v.trim())
      .filter(Boolean);

    const seen = new Set<string>();
    const details = rawDetails.filter(v => {
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    });

    setData({ imgSrc: img, imgAlt: title, title, details });
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const updateFromIndex = useCallback((idx: number) => {
    const list = itemsRef.current as any[];
    if (!list.length) return;
    // Wrap around
    const len = list.length;
    const newIdx = ((idx % len) + len) % len;
    indexRef.current = newIdx;
    const el = list[newIdx];
    if (!el) return;
    const get = (attr: string) => el.getAttribute(attr) || '';
    const img = get('data-img');
    const title = get('data-title');
    const category = get('data-category');

    // Use the actual category from the image
    const rawDetails = [
      get('data-medium'),
      get('data-size'),
      get('data-year'),
      category, // actual category
    ]
      .map(v => v.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    const details = rawDetails.filter(v => {
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    });
    setData({ imgSrc: img, imgAlt: title, title, details });
  }, []);

  const next = useCallback(() => {
    if (!itemsRef.current.length) return;
    updateFromIndex(indexRef.current + 1);
  }, [updateFromIndex]);

  const prev = useCallback(() => {
    if (!itemsRef.current.length) return;
    updateFromIndex(indexRef.current - 1);
  }, [updateFromIndex]);

  // Click-away close behavior (like window.onclick targeting the modal backdrop)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close, next, prev]);

  return useMemo(
    () => ({ isOpen, data, openFromElement, close, next, prev }),
    [isOpen, data, openFromElement, close, next, prev]
  );
}

export default useGalleryModal;
